// The progress of this browser: rites won, rites in play, glyphs seen, laws
// witnessed, notes, the Order and fragments found. Kept in the
// browser (src/boundary/save.ts reads and writes it). Every change makes a
// new value.

import { heldCards, seen, type Column, type TableState } from "./engine.ts"
import type { SavedPlay } from "./game.ts"
import type { WonKey } from "./rite.ts"
import { keyOf, type GlyphKey } from "./rings.ts"
import type { FragmentId } from "../config/fragments.ts"
import type { LawId } from "../config/laws.ts"

export type Millis = number

export type Best = {
  readonly moves: number
  readonly aid: number
  readonly pure: boolean // won once with no help at all
  readonly at: Millis
}
export type Witnessed = { readonly n: number; readonly example: readonly GlyphKey[] }
export type NoteKey = `glyph:${GlyphKey}`

export type Progress = {
  readonly solved: Readonly<Partial<Record<WonKey, Best>>>
  readonly playing: Readonly<Partial<Record<WonKey, SavedPlay>>>
  readonly seen: Readonly<Partial<Record<GlyphKey, true>>>
  readonly laws: Readonly<Partial<Record<LawId, Witnessed>>>
  readonly notes: Readonly<Partial<Record<NoteKey, string>>>
  readonly order: Readonly<Partial<Record<GlyphKey, true>>>
  readonly fragments: Readonly<Partial<Record<FragmentId, Millis>>>
  readonly rosetta: boolean
}

export const EMPTY_PROGRESS: Progress = {
  solved: {},
  playing: {},
  seen: {},
  laws: {},
  notes: {},
  order: {},
  fragments: {},
  rosetta: false,
}

// A record without one key, keeping its type.
export const without = <K extends string, V>(rec: Readonly<Partial<Record<K, V>>>, key: K): Readonly<Partial<Record<K, V>>> => {
  const out: Partial<Record<K, V>> = {}
  for (const [k, v] of entriesOf(rec)) if (k !== key) out[k] = v
  return out
}

const entriesOf = <K extends string, V>(rec: Readonly<Partial<Record<K, V>>>): readonly (readonly [K, V])[] => {
  const out: (readonly [K, V])[] = []
  for (const k in rec) {
    const v = rec[k]
    if (v !== undefined) out.push([k, v])
  }
  return out
}

export const keysOf = <K extends string, V>(rec: Readonly<Partial<Record<K, V>>>): readonly K[] => entriesOf(rec).map(([k]) => k)
export const valuesOf = <K extends string, V>(rec: Readonly<Partial<Record<K, V>>>): readonly V[] => entriesOf(rec).map(([, v]) => v)

// ---------- small changes ----------

const columnKeys = (col: Column): readonly GlyphKey[] => col.cards.map((c) => keyOf(seen(c, col.mirror)))

// Every glyph showing on the table, as the player sees it.
export const glyphsShowing = (state: TableState): readonly GlyphKey[] => [
  ...state.cols.flatMap(columnKeys),
  ...heldCards(state.altars).map((c) => keyOf(c.rings)),
]

export const noteSeen = (progress: Progress, state: TableState): Progress => {
  const fresh = glyphsShowing(state).filter((k) => progress.seen[k] === undefined)
  if (fresh.length === 0) return progress
  return { ...progress, seen: { ...progress.seen, ...Object.fromEntries(fresh.map((k) => [k, true] as const)) } }
}

export type Witnessing = { readonly progress: Progress; readonly first: boolean }

// A law seen at work, with the glyphs of that moment. An example is always
// glyph names, never card numbers (the old Codex crashed on card numbers).
export const witness = (progress: Progress, law: LawId, example: readonly GlyphKey[]): Witnessing => {
  const before = progress.laws[law]
  const n = (before?.n ?? 0) + 1
  const kept = example.length > 0 ? example : (before?.example ?? [])
  return { progress: { ...progress, laws: { ...progress.laws, [law]: { n, example: kept } } }, first: before === undefined }
}

export const setPlaying = (progress: Progress, key: WonKey, saved: SavedPlay): Progress => ({
  ...progress,
  playing: { ...progress.playing, [key]: saved },
})

export const dropPlaying = (progress: Progress, key: WonKey): Progress => ({ ...progress, playing: without(progress.playing, key) })

export const inPlay = (progress: Progress, key: WonKey): boolean => (progress.playing[key]?.moves.length ?? 0) > 0

export const setNote = (progress: Progress, key: NoteKey, text: string): Progress => ({ ...progress, notes: { ...progress.notes, [key]: text } })

export const placeGlyph = (progress: Progress, key: GlyphKey): Progress => ({ ...progress, order: { ...progress.order, [key]: true } })

export const setRosetta = (progress: Progress, on: boolean): Progress => ({ ...progress, rosetta: on })
